import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTrashAlt, FaPlus } from 'react-icons/fa';
import './TagsPage.css'; // 导入独立的CSS文件

function TagsPage({ conditionLabels, rawConditions, onConditionsUpdated }) {
  const location = useLocation();
  
  // 从路由状态中获取初始条件
  const [strictConditions, setStrictConditions] = useState(
    location.state?.strictConditions || []
  );
  const [flexibleConditions, setFlexibleConditions] = useState(
    location.state?.flexibleConditions || []
  );
  const [newConditionKey, setNewConditionKey] = useState('');
  const [newConditionValue, setNewConditionValue] = useState('');
  const [valueOptions, setValueOptions] = useState([]);
  const [inputType, setInputType] = useState('select'); // 'select' 或 'number'
  const conditionIdCounterRef = useRef(0);
  const navigate = useNavigate();

  // 被视为"无所谓"的值，应该被过滤掉
  const indifferentValues = ['无所谓', '都行', 'any', '不限'];
  // 特定键，其中"都行"可能是有意义的（如外貌）
  const meaningfulIndifferentKeys = ['appearance', 'property'];

  // --- 计算属性 ---
  const generatedConditionsCount = strictConditions.length + flexibleConditions.length;

  // 条件值选项映射
  const conditionValueOptions = {
    appearance: ['至少不难看', '必须好看'],
    skin: ['一般', '白皙', '很好'],
    personality: ['内向', '外向'],
    property: ['必须有房'],
    education_min: ['高中', '本科', '硕士', '博士'],
    parent_education_min: ['高中', '本科', '硕士', '博士'],
    drinking: ['能接受偶尔', '只接受从不'],
    smoking: ['能接受偶尔', '只接受从不'],
  };

  // 需要数字输入的条件
  const numberInputConditions = ['height_min', 'height_max', 'age_min', 'age_max', 'annual_income_min'];

  // 当条件类型改变时，更新可选值
  useEffect(() => {
    if (!newConditionKey) {
      setValueOptions([]);
      setInputType('select');
      return;
    }

    if (numberInputConditions.includes(newConditionKey)) {
      setInputType('number');
      setValueOptions([]);
      setNewConditionValue('');
    } else if (conditionValueOptions[newConditionKey]) {
      setInputType('select');
      setValueOptions(conditionValueOptions[newConditionKey]);
      setNewConditionValue(conditionValueOptions[newConditionKey][0] || '');
    } else {
      setInputType('text');
      setValueOptions([]);
      setNewConditionValue('');
    }
  }, [newConditionKey]);

  // 格式化值的函数
  function formatValue(key, value) {
    // 处理特定格式化
    switch(key) {
      case 'height_min':
      case 'height_max':
        return `${value} cm`;
      case 'annual_income_min':
        return `${value} 万`;
      case 'personality':
        if (value === 'e') return '外向';
        if (value === 'i') return '内向';
        return '不限';
      case 'education_min':
      case 'parent_education_min':
        return value;
      default:
        return value;
    }
  }

  // 处理原始条件数据，生成标签
  // 修改useEffect部分，不再自动从rawConditions生成条件
  React.useEffect(() => {
    // 只有当rawConditions有值且当前没有条件时才初始化
    if (rawConditions && Object.keys(rawConditions).length > 0 && 
        strictConditions.length === 0 && flexibleConditions.length === 0) {
      const newTags = [];
      for (const key in rawConditions) {
        if (Object.hasOwnProperty.call(rawConditions, key) && conditionLabels[key]) {
          const rawValue = rawConditions[key];
          const valueStr = String(rawValue).toLowerCase();
  
          const isIndifferent = indifferentValues.includes(valueStr);
          const isAllowedIndifferent = meaningfulIndifferentKeys.includes(key);
  
          if (!isIndifferent || isAllowedIndifferent) {
            conditionIdCounterRef.current++;
            newTags.push({
              id: conditionIdCounterRef.current,
              key: key,
              label: conditionLabels[key],
              value: formatValue(key, rawValue),
              rawValue: rawValue
            });
          }
        }
      }
      setFlexibleConditions(newTags);
    }
  }, [rawConditions, conditionLabels]);

  // 修改导航函数，确保在导航前保存条件
  const goToCardFlow = () => {
    if (onConditionsUpdated) {
      onConditionsUpdated(strictConditions, flexibleConditions);
    }
    navigate('/cards');
  };

  function deleteCondition(conditionId) {
    setStrictConditions(prev => prev.filter(c => c.id !== conditionId));
    setFlexibleConditions(prev => prev.filter(c => c.id !== conditionId));
  }

  function addManualCondition() {
    if (!newConditionKey || !newConditionValue) return;

    const key = newConditionKey;
    let value = newConditionValue;
    
    // 处理特殊值转换
    if (key === 'personality') {
      if (value === '内向') value = 'i';
      else if (value === '外向') value = 'e';
    }
    
    const label = conditionLabels[key] || key;
    const formattedDispValue = formatValue(key, value);

    // 检查是否已存在相同类型的条件
    const existingConditionIndex = flexibleConditions.findIndex(c => c.key === key);
    const existingStrictConditionIndex = strictConditions.findIndex(c => c.key === key);
    
    // 创建新条件
    conditionIdCounterRef.current++;
    const newCondition = {
      id: conditionIdCounterRef.current,
      key: key,
      label: label,
      value: formattedDispValue,
      rawValue: value
    };

    // 如果在宽松条件中存在相同类型的条件，则替换它
    if (existingConditionIndex !== -1) {
      const newFlexibleConditions = [...flexibleConditions];
      newFlexibleConditions.splice(existingConditionIndex, 1, newCondition);
      setFlexibleConditions(newFlexibleConditions);
    } 
    // 如果在卡死条件中存在相同类型的条件，则删除卡死条件中的条件并添加新条件到宽松条件
    else if (existingStrictConditionIndex !== -1) {
      const newStrictConditions = [...strictConditions];
      newStrictConditions.splice(existingStrictConditionIndex, 1);
      setStrictConditions(newStrictConditions);
      setFlexibleConditions(prev => [...prev, newCondition]);
    } 
    // 如果不存在相同类型的条件，则直接添加
    else {
      setFlexibleConditions(prev => [...prev, newCondition]);
    }

    // 清除输入
    setNewConditionKey('');
    setNewConditionValue('');
  }

  // 同样需要修改拖拽结束处理函数，确保不会有重复条件
  const onDragEnd = (result) => {
    const { source, destination } = result;

    // 如果没有目的地（拖出了可放置区域）或者源和目的地相同，则不做任何操作
    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    // 处理删除区域
    if (destination.droppableId === 'delete-zone') {
      // 从源列表中删除项目
      if (source.droppableId === 'strict') {
        const newStrict = Array.from(strictConditions);
        const [removed] = newStrict.splice(source.index, 1);
        setStrictConditions(newStrict);
      } else if (source.droppableId === 'flexible') {
        const newFlexible = Array.from(flexibleConditions);
        const [removed] = newFlexible.splice(source.index, 1);
        setFlexibleConditions(newFlexible);
      }
      return;
    }

    // 处理在列表之间移动
    if (source.droppableId !== destination.droppableId) {
      // 检查是否尝试将条件移动到已满的卡死条件区域
      if (destination.droppableId === 'strict' && strictConditions.length >= 3) {
        // 卡死条件已达到最大数量，不执行移动操作
        return;
      }
      
      // 从源列表中移除
      let sourceList, destinationList;
      if (source.droppableId === 'strict') {
        sourceList = Array.from(strictConditions);
        destinationList = Array.from(flexibleConditions);
        const [removed] = sourceList.splice(source.index, 1);
        destinationList.splice(destination.index, 0, removed);
        setStrictConditions(sourceList);
        setFlexibleConditions(destinationList);
      } else {
        sourceList = Array.from(flexibleConditions);
        destinationList = Array.from(strictConditions);
        const [removed] = sourceList.splice(source.index, 1);
        destinationList.splice(destination.index, 0, removed);
        setFlexibleConditions(sourceList);
        setStrictConditions(destinationList);
      }
    } else {
      // 在同一列表内移动
      if (source.droppableId === 'strict') {
        const newStrict = Array.from(strictConditions);
        const [removed] = newStrict.splice(source.index, 1);
        newStrict.splice(destination.index, 0, removed);
        setStrictConditions(newStrict);
      } else {
        const newFlexible = Array.from(flexibleConditions);
        const [removed] = newFlexible.splice(source.index, 1);
        newFlexible.splice(destination.index, 0, removed);
        setFlexibleConditions(newFlexible);
      }
    }
  };



  // 在组件返回的JSX中添加导航按钮
  return (
  <div className="app-container">
  <h1 className="title">管理您的条件标签</h1>
  
  {/* 添加导航按钮 */}
  <div className="navigation-buttons">
  <button className="nav-button" onClick={goToCardFlow}>
  开始遇见
  </button>
  </div>
  
  {/* 现有的DragDropContext和其他内容 */}
  <DragDropContext onDragEnd={onDragEnd}>
  <div className="conditions-container">
  <div className="conditions-box strict-box">
  <h2 className="box-title">
  卡死条件
  <span className="box-count">({strictConditions.length}/3)</span>
  </h2>
  <Droppable droppableId="strict" isDropDisabled={strictConditions.length >= 3}>
  {(provided, snapshot) => (
  <div
  className={`drag-area ${snapshot.isDraggingOver ? 'is-dragging-over' : ''} ${strictConditions.length >= 3 ? 'is-full' : ''}`}
  ref={provided.innerRef}
  {...provided.droppableProps}
  >
  {strictConditions.length === 0 && (
  <div className="empty-state">从下方拖拽条件至此</div>
  )}
  {strictConditions.length >= 3 && strictConditions.length > 0 && (
  <div className="limit-notice">卡死条件已达上限</div>
  )}
  {strictConditions.map((condition, index) => (
  <Draggable
  key={`strict-${condition.id}`}
  draggableId={`strict-${condition.id}`}
  index={index}
  >
  {(provided, snapshot) => (
  <div
  className="condition-tag"
  ref={provided.innerRef}
  {...provided.draggableProps}
  {...provided.dragHandleProps}
  >
  <span>{condition.label}: {condition.value}</span>
  {/* 删除了删除按钮 */}
  </div>
  )}
  </Draggable>
  ))}
  {provided.placeholder}
  </div>
  )}
  </Droppable>
  </div>
  
  <div className="conditions-box flexible-box">
  <h2 className="box-title">
  宽松条件
  <span className="box-count">({flexibleConditions.length})</span>
  </h2>
  <Droppable droppableId="flexible">
  {(provided, snapshot) => (
  <div
  className={`drag-area ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
  ref={provided.innerRef}
  {...provided.droppableProps}
  >
  {flexibleConditions.length === 0 && generatedConditionsCount > 0 && (
  <div className="empty-state">从下方拖拽条件至此 或 从 "卡死条件" 拖拽至此</div>
  )}
  {flexibleConditions.length === 0 && generatedConditionsCount === 0 && (
  <div className="empty-state">生成条件后会出现在这里，或从 "卡死条件" 拖拽至此</div>
  )}
  {flexibleConditions.map((condition, index) => (
  <Draggable
  key={`flexible-${condition.id}`}
  draggableId={`flexible-${condition.id}`}
  index={index}
  >
  {(provided, snapshot) => (
  <div
  className="condition-tag"
  ref={provided.innerRef}
  {...provided.draggableProps}
  {...provided.dragHandleProps}
  >
  <span>{condition.label}: {condition.value}</span>
  {/* 删除了删除按钮 */}
  </div>
  )}
  </Draggable>
  ))}
  {provided.placeholder}
  </div>
  )}
  </Droppable>
  </div>
  
  {/* 垃圾桶图标放在条件框外部 */}
  <Droppable droppableId="delete-zone">
  {(provided, snapshot) => (
  <div
  className={`trash-icon-container ${snapshot.isDraggingOver ? 'is-dragging-over' : ''}`}
  ref={provided.innerRef}
  {...provided.droppableProps}
  >
  <FaTrashAlt className="trash-icon" />
  {provided.placeholder}
  </div>
  )}
  </Droppable>
  </div>
  </DragDropContext>
  
  <div className="manual-add-section">
  <h2>手动添加条件</h2>
  <div className="add-condition-form">
  <select 
  className="select"
  value={newConditionKey} 
  onChange={(e) => setNewConditionKey(e.target.value)}
  >
  <option disabled value="">选择条件类型</option>
  {Object.entries(conditionLabels).map(([key, label]) => (
  <option key={key} value={key}>{label}</option>
  ))}
  </select>
  
  {inputType === 'select' && valueOptions.length > 0 ? (
  <select
  className="input"
  value={newConditionValue}
  onChange={(e) => setNewConditionValue(e.target.value)}
  >
  {valueOptions.map((option) => (
  <option key={option} value={option}>{option}</option>
  ))}
  </select>
  ) : inputType === 'number' ? (
  <input 
  className="input"
  type="number" 
  value={newConditionValue} 
  onChange={(e) => setNewConditionValue(e.target.value)} 
  placeholder={`输入${conditionLabels[newConditionKey] || ''}数值`}
  min={0}
  />
  ) : (
  <input 
  className="input"
  type="text" 
  value={newConditionValue} 
  onChange={(e) => setNewConditionValue(e.target.value)} 
  placeholder="输入条件值"
  disabled={!newConditionKey}
  />
  )}
  
  <button 
  className="add-button"
  onClick={addManualCondition} 
  disabled={!newConditionKey || !newConditionValue}
  title="添加到宽松条件"
  >
  <FaPlus />
  </button>
  </div>
  </div>
  </div>
  );
}

export default TagsPage;